import { Component } from 'substance'
import { BarItemComponent } from '../bar-item/BarItemComponent'
import './scss/popover.scss'
import { Event } from '../../../../../lib/constants/Event'

/**
 * A topbar rendered icon, text or image.
 * Either a title, icon or button can be used as item rendered in the top bar.
 *
 * @typedef Popover
 * @property {string} title - Text title of text bar item
 * @property {string} icon - Can be either an FontAwesome icon or image url
 * @property {string} button - Text to be used on button
 * @property {string} statusText - Status text to display next to a button
 * @property {string} css - Custom css string to apply to bar item
 * @property {boolean} sticky - Whether it should remain if user clicks elsewhere
 * @property {boolean} enabled - Whether it is enabled or not
 * @property {boolean} active - Whether it is active, selected, or not
 * @property {boolean} isSaving - True if writer is currently saving article
 *
 * @typedef Component.PopoverComponent.Props
 * @property {Popover} popover - Popover configuration object
 *
 * @param {Component.PopoverComponent.Props} props
 * @property {Component.PopoverComponent.Props} props
 *
 * @memberof Component
 */
class PopoverComponent extends Component {

    constructor(...args) {
        super(...args)
    }

    dispose() {
        super.dispose()
        this.context.api.events.off('__popover-' + this.props.popover.id, Event.BROWSER_RESIZE)
        this.context.api.events.off('__popover-' + this.props.popover.id, 'popover:close')
        document.removeEventListener('click', this._clickListener)
    }

    getInitialState() {
        return {
            enabled: true,
            active: false,
            icon: this.props.popover.icon || null,
            title: this.props.popover.title || null,
            button: this.props.popover.button || null,
            statusText: this.props.popover.statusText || null,
            triggerElement: null
        }
    }

    didMount() {
        document.addEventListener(
            'click',
            this._clickListener.bind(this),
            false
        )

        this.context.api.events.on('__popover-' + this.props.popover.id, Event.BROWSER_RESIZE, () => {
            this.recalculateOffsets()
        })

        this.context.api.events.on('__popover-' + this.props.popover.id, 'popover:close', () => {
            this.extendState({ active: false })
        })
    }

    _clickListener(e) {
        // Listen to clicks outside of the popover only so that we
        // can close non sticky popovers automatically
        if (!this.state.active || this.props.popover.sticky === true) {
            return
        }

        if (!this.refs.popover.el.el.contains(e.target)) {
            this.extendState({ active: false })
        }
    }

    render($$) {
        let barContainerEl = $$('div')
            .addClass('sc-np-bar-container')

        if (this.state.button) {
            barContainerEl.append(
                this.renderTriggerButton($$)
            )
        }
        else if (this.state.icon || this.state.title) {
            barContainerEl.append(
                this.renderTriggerIcon($$)
            )
        }

        if (this.state.statusText) {
            barContainerEl.append(
                this.renderStatusText($$)
            )
        }

        return $$('div').append([
            barContainerEl,
            this.renderPopoverContent($$)
        ])
    }

    renderTriggerButton($$) {
        const UIButton = this.context.componentRegistry.get('UIButton')
        return $$(UIButton, {
            contextIcon: this.state.icon,
            label: this.state.button,
            enabled: this.state.enabled,
            contextClick: (evt) => this.togglePopover(evt, this.props.popover.id),
            onClick: (evt) => this.buttonClick(evt, this.props.popover.id)
        })
            .ref('btn')
    }

    renderTriggerIcon($$) {
        return $$(BarItemComponent, {
            icon: this.state.icon,
            title: this.state.title,
            css: this.props.popover.css,
            enabled: this.state.enabled,
            active: this.state.active
        })
            .ref('icon')
            .on('click', (evt) => this.togglePopover(evt))
    }

    renderStatusText($$) {
        if (!this.state.statusText.title) {
            return $$('p')
                .ref('statustext')
                .addClass('sc-np-bar-item')
                .append(this.state.statusText)
        }

        const pEl = $$('p')
            .ref('statustext')
            .addClass('sc-np-bar-item')

        if (this.state.statusText.title) {
            let titleEl = $$('span').append(this.state.statusText.title + " ")
            let textEl = null

            if (this.state.statusText.text) {
                titleEl.addClass('sc-np-bar-item-title')

                textEl = $$('span')
                    .addClass('sc-np-bar-item-text')
                    .append(
                        this.state.statusText.text
                    )
            }

            pEl.append([titleEl, textEl])
        }
        else {
            pEl.append(
                this.state.statusText
            )
        }

        return pEl
    }

    renderPopoverContent($$) {
        var el = $$('div')
            .ref('popover')
            .addClass('sc-np-popover')
            .append(
                $$('div')
                    .ref('popover_top')
                    .addClass('sc-np-popover-arrow')
            )
            .append(
                $$(this.props.popover.component, {
                    popover: {
                        setStatusText: (statusText) => this.onSetStatusText(statusText),
                        setButtonText: (buttonText) => this.onSetButtonText(buttonText),
                        setTitle: (title) => this.onSetTitle(title),
                        setIcon: (iconClass) => this.onSetIcon(iconClass),
                        disable: () => this.onSetEnabled(false),
                        enable: () => this.onSetEnabled(true),
                        close: () => {
                            if (this.state.active) {
                                this.togglePopover()
                            }
                        },
                        active: this.state.active
                    },
                    isSaving: this.props.isSaving
                })
                    .ref('component')
            )
            .css('left', '-99999px')

        if (this.state.active === true) {
            el.addClass('active');
            window.setTimeout(() => {
                this.positionPopover(
                    this.getOffsets()
                )
            }, 5)
        }

        return el
    }

    positionPopover(offset) {
        let popover = this.el.el.querySelector("div.sc-np-popover"),
            arrow = this.el.el.querySelector("div.sc-np-popover-arrow")

        popover.style.left = offset.box + 'px'
        arrow.style.marginLeft = offset.arrow + 'px'
    }

    buttonClick(evt) {
        if (this.refs['component'].defaultAction) {
            this.refs['component'].defaultAction(evt)
        }
    }

    togglePopover(evt) {
        if (this.state.active === true) {
            this.extendState({ active: false })
        }
        else {
            let triggerElement = null

            if (evt.target.nodeName === 'A') {
                triggerElement = evt.target
            }
            else if (evt.currentTarget.nodeName === 'SPAN' && evt.currentTarget.classList.contains('trigger')) {
                triggerElement = evt.currentTarget
            }
            else if (evt.currentTarget.nodeName === 'BUTTON') {
                triggerElement = evt.currentTarget
            }
            else {
                return
            }

            this.extendState({
                offsetLeft: triggerElement.offsetLeft,
                offsetWidth: triggerElement.offsetWidth,
                active: true
            })

            // Opening one popover closes others, even the sticky ones, so
            // send an internal close event to all other popovers
            this.context.api.events.trigger(
                '__popover-' + this.props.popover.id,
                'popover:close'
            )

        }

        // Must prevent default and propagation for automatic closing
        // functionality on click outside of the popover
        if (evt) { // Check if it's a mouse event that triggers this method
            evt.preventDefault()
            evt.stopPropagation()
        }

    }

    onSetIcon(iconClass) {
        let old = this.state.iconClass
        this.extendState({
            icon: iconClass
        })

        this.onLayoutChange(iconClass, old)
    }

    onSetStatusText(text) {
        let old = this.state.statusText
        this.extendState({
            statusText: text
        })

        this.onLayoutChange(text, old)
    }

    onSetButtonText(text) {
        let old = this.state.button
        this.extendState({
            button: text
        })

        this.onLayoutChange(text, old)
    }

    onSetTitle(title) {
        let old = this.state.title
        this.extendState({
            title: title
        })

        this.onLayoutChange(title, old)
    }

    onSetEnabled(enabled) {
        this.extendState({
            enabled: enabled
        })
    }

    onLayoutChange(newValue, oldValue) {
        if (newValue === oldValue) {
            return
        }

        // Send browser resize event to enforce position recalculations
        // for all active (visible) popovers
        this.context.api.events.trigger(
            '__popover',
            Event.BROWSER_RESIZE
        )
    }

    /*
     * Calculate offsets for popover box (left) and it's arrow (margin)
     */
    getOffsets(offsetLeft, offsetWidth) {
        if (!offsetLeft) {
            offsetLeft = this.state.offsetLeft
        }

        if (!offsetWidth) {
            offsetWidth = this.state.offsetWidth
        }

        let popoverEl = this.refs['popover'].el,
            left = offsetLeft - (popoverEl.width / 2) + offsetWidth / 2,
            margin = 0

        if (left < 10) {
            margin = left - 10
            left = 10
        }
        else if (left + popoverEl.width > document.body.clientWidth) {
            let oldLeft = left
            left = document.body.clientWidth - popoverEl.width - 10
            margin = oldLeft - left
        }

        return {
            box: left,
            arrow: margin
        }
    }

    recalculateOffsets() {
        let triggerElement

        if (this.state.active) {
            triggerElement = this.el.el.querySelector('.sc-np-bar-container > .sc-np-bar-item')
            if (null === triggerElement) {
                let triggerElements = this.el.el.querySelectorAll('.sc-np-bar-container .np-ui-btn')
                if (triggerElements.length === 2) {
                    triggerElement = triggerElements[1]
                }
            }

            this.positionPopover(
                this.getOffsets(triggerElement.offsetLeft, triggerElement.offsetWidth)
            )
        }
    }
}

export { PopoverComponent }
